"use client";

import { useCommonAccountModal } from "@/Areas/Common/Pages/CommonAccount";
import { SmartLink } from "@/Components";
import { Helper, Roles } from "@/Helpers";
import { CenterOptions } from "@/Helpers/CenterOptions";
import {
  useAccount,
  useApp,
  useCheckRole,
  useImageFallback,
  useResponsive,
  useServerRequestContext,
  useT,
} from "@/Hooks";
import { Avatar, Button, Dropdown, Space, Spin, Typography } from "antd";
import { FC } from "react";
import {
  RiAccountCircleLine,
  RiBuildingLine,
  RiHomeLine,
  RiLockPasswordLine,
  RiLogoutBoxRLine,
  RiUserLine,
  RiUserStarLine,
} from "react-icons/ri";
import HeaderCartButton from "./HeaderCartButton";

export interface HeaderAccountActionsProps {
  compact?: boolean;
}

const HeaderAccountActions: FC<HeaderAccountActionsProps> = ({
  compact = false,
}) => {
  const { profile, isSignIn, loading, signOut, isCenterPersonnel } =
    useAccount();
  const t = useT();
  const commonAccountModal = useCommonAccountModal();
  const { modal } = useApp();
  const { isMobile } = useResponsive();
  const serverRequestContext = useServerRequestContext();
  const isDoctor = useCheckRole(Roles.Provider);
  const getImageFallback = useImageFallback();
  return (
    <Space size={compact ? "small" : "middle"} wrap>
      {loading ? (
        <Spin size="small" />
      ) : isSignIn ? (
        <>
          <Dropdown
            menu={{
              items: [
                ...Helper.addItem(
                  {
                    key: "Panel",
                    icon: <RiHomeLine />,
                    label: (
                      <SmartLink href="/Panel">
                        {t(
                          "landing.header.personnelArea",
                          undefined,
                          "Personnel area",
                        )}
                      </SmartLink>
                    ),
                  },
                  isCenterPersonnel &&
                  serverRequestContext.CenterTypeKey !=
                  CenterOptions.Enum.CenterType.Normal.key,
                ),
                {
                  key: "Customer",
                  icon: <RiHomeLine />,
                  label: (
                    <SmartLink href="/Customer/Dashboard">
                      {t(
                        "landing.header.customerArea",
                        undefined,
                        "Customer area",
                      )}
                    </SmartLink>
                  ),
                },
                {
                  key: "profile",
                  icon: <RiUserLine />,
                  label: (
                    <SmartLink
                      href={
                        isCenterPersonnel
                          ? serverRequestContext.CenterTypeKey ==
                            CenterOptions.Enum.CenterType.Normal.key
                            ? "/personnel-area-unavailable"
                            : "/Panel/Common/Account/Profile"
                          : "/Customer/Account/Profile"
                      }
                    >
                      {t(
                        "landing.header.editProfile",
                        undefined,
                        "Edit profile",
                      )}
                    </SmartLink>
                  ),
                },
                {
                  key: "accounts",
                  icon: <RiAccountCircleLine />,
                  label: t(
                    "commonAccount.browserAccount.title",
                    undefined,
                    "Accounts",
                  ),
                  onClick: () => commonAccountModal.showBrowserAccounts(),
                },
                ...Helper.addItem(
                  {
                    key: "password",
                    icon: <RiLockPasswordLine />,
                    label: t(
                      "landing.header.passwordActions",
                      undefined,
                      "Password actions",
                    ),
                    onClick: () => commonAccountModal.showPasswordSetting(),
                  },
                  isCenterPersonnel,
                ),
                ...Helper.addItem(
                  {
                    key: "DoctorProfile",
                    icon: <RiUserStarLine />,
                    label: (
                      <SmartLink href={profile?.CenterUser?.Link}>
                        {t("landing.header.myProfile", undefined, "My profile")}
                      </SmartLink>
                    ),
                  },
                  isDoctor && !!profile?.CenterUser?.Link,
                ),
                ...Helper.addItem(
                  {
                    key: "CenterProfile",
                    icon: <RiBuildingLine />,
                    label: (
                      <SmartLink href={profile?.Center?.Link}>
                        {t(
                          "landing.header.centerProfile",
                          undefined,
                          "Center profile",
                        )}
                      </SmartLink>
                    ),
                  },
                  !!profile?.Center?.Link,
                ),
                {
                  key: "logout",
                  icon: <RiLogoutBoxRLine />,
                  label: t("landing.header.signOut", undefined, "Sign out"),
                  onClick: async () => {
                    if (
                      await modal.confirm({
                        title: t(
                          "landing.header.signOut",
                          undefined,
                          "Sign out",
                        ),
                        content: t(
                          "landing.header.signOutConfirm",
                          undefined,
                          "Do you want to sign out of your account?",
                        ),
                        okText: t("landing.common.yes", undefined, "Yes"),
                        cancelText: t("landing.common.no", undefined, "No"),
                        centered: true,
                      })
                    ) {
                      signOut();
                    }
                  },
                },
              ],
            }}
          >
            {compact ? (
              <Avatar
                size="small"
                src={
                  profile
                    ? getImageFallback({
                      imageUrl: profile.ImageUrl,
                      entity: "User",
                      genderKey: profile.GenderKey,
                      isDoctor,
                    })
                    : undefined
                }
              />
            ) : (
              profile &&
              (isMobile ? (
                <Avatar
                  src={getImageFallback({
                    imageUrl: profile.ImageUrl,
                    entity: "User",
                    genderKey: profile.GenderKey,
                    isDoctor,
                  })}
                />
              ) : (
                <Button
                  icon={
                    <Avatar
                      src={getImageFallback({
                        imageUrl: profile.ImageUrl,
                        entity: "User",
                        genderKey: profile.GenderKey,
                        isDoctor,
                      })}
                    />
                  }
                >
                  <Typography.Text>{profile.FullName}</Typography.Text>
                </Button>
              ))
            )}
          </Dropdown>
          <HeaderCartButton compact={compact} />
        </>
      ) : (
        <button
          className={compact ? "btn btn-sm log-btn" : "btn log-btn"}
          onClick={() => commonAccountModal.showSignIn()}
        >
          {t("landing.header.signInOrSignUp", undefined, "Sign in / Sign up")}
        </button>
      )}
    </Space>
  );
};

export default HeaderAccountActions;
